import { useMemo, useCallback } from "react";
import {
  Box,
  EntitySelector,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorEmpty,
  Icon,
  Text,
  useEntitySelectorSelections,
  type EntitySelectorConfig,
  type EntitySelectorContentSize,
  type EntitySelectorVariant,
  type EntitySelection,
  type IconName,
} from "@salestrak/ui";
import { useRolePool } from "./hooks";
import { toEntityTriggerSize, type SelectorSize } from "./sizes";
import type { Omittable } from "@salestrak/ui";

// Static blurbs for the five seeded system roles, keyed by lowercased
// role name. Custom roles fall back to a generic line.
const ROLE_DESCRIPTIONS: Record<string, string> = {
  owner: "Full access to everything in your workspace.",
  admin: "Can manage members, settings, and all items in your workspace.",
  member: "Can access all public items in your workspace.",
  "limited member": "Can only access items shared with them.",
  guest: "Can only access items shared with them.",
};
const CUSTOM_ROLE_DESCRIPTION = "Custom role for your workspace.";

/**
 * Rich trigger for prominent placements (e.g. the invite dialog's
 * "Invite as" section): padded icon tile, bold role name with an inline
 * caret, and a description line — instead of the boxed input trigger.
 */
function RoleSelectorDetailedTrigger({
  placeholder,
  disabled,
}: Omittable<{
  placeholder: string;
  disabled?: boolean;
}>) {
  const selections = useEntitySelectorSelections();
  const selected = selections[0]?.entity;
  const description = selected
    ? (ROLE_DESCRIPTIONS[selected.title.trim().toLowerCase()] ??
      CUSTOM_ROLE_DESCRIPTION)
    : null;

  // Disabled keeps the content visible at full strength but drops every
  // interactive affordance — no carets, no press, no trigger wrapper.
  const row = (
    <Box
      as={disabled ? "div" : "button"}
      direction="row"
      align="center"
      gap="md"
      paddingX="sm"
      paddingY="xs"
      rounded="md"
      fill="width"
      cursor={disabled ? undefined : "pointer"}
      hoverBackground={!(disabled)}
    >
      <Box padding="sm" rounded="md" background="muted">
        <Icon name="circle-user" size="lg" color="muted" />
      </Box>
      <Box flex={1} minWidth={0} gap="2xs" align="start">
        <Box direction="row" align="center" gap="2xs">
          <Text
            size="sm"
            weight="semibold"
            variant={disabled ? "muted" : undefined}
            truncate={1}
          >
            {selected?.title ?? placeholder}
          </Text>
          {!disabled && <Icon name="chevron-down" size="xs" color="muted" />}
        </Box>
        {description && (
          <Text size="sm" variant="muted" truncate={1}>
            {description}
          </Text>
        )}
      </Box>
      {!disabled && <Icon name="chevron-down" size="sm" color="muted" />}
    </Box>
  );

  if (disabled) return row;

  return <EntitySelectorTrigger render={row} />;
}

export type RoleSelectorProps = Omittable<{
  /** Currently selected role ID */
  value?: string | null;
  /** Called when a role is selected */
  onValueChange?: (roleId: string) => void;
  /** Only show roles at or below this hierarchy level */
  maxHierarchy?: number;
  /** Restrict the pool to this set of role IDs (e.g. invitable-roles list). */
  allowedRoleIds?: readonly string[];
  /** "popover" (default) wraps in popover; "inline" renders content directly */
  variant?: EntitySelectorVariant;
  /** Rich trigger: icon tile + bold role name + description line instead of the boxed input. */
  detailed?: boolean;
  placeholder?: string;
  size?: SelectorSize;
  disabled?: boolean;
  chips?: boolean;
  icon?: IconName;
  fill?: boolean;
  contentFit?: boolean;
  contentSize?: EntitySelectorContentSize;
  children?: React.ReactNode;
}>;

export function RoleSelector({
  value,
  onValueChange,
  maxHierarchy,
  allowedRoleIds,
  variant,
  detailed = false,
  placeholder = "Select role...",
  size,
  disabled,
  chips,
  icon,
  fill,
  contentFit = true,
  contentSize,
  children,
}: RoleSelectorProps) {
  const { pool } = useRolePool({ maxHierarchy, allowedRoleIds });

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 1,
      searchPlaceholder: "Search roles...",
      emptyMessage: "No roles found",
      noItemsMessage: "No roles yet",
    }),
    [pool],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    if (!value) return [];
    const entity = pool.items.find((item) => item.id === value);
    if (!entity) return [];
    return [{ id: value, poolId: pool.id, entity }];
  }, [value, pool]);

  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      const selected = next[0];
      if (selected) onValueChange?.(selected.id);
    },
    [onValueChange],
  );

  return (
    <EntitySelector variant={variant} config={config} value={selections} onValueChange={handleValueChange}>
      {children ?? (
        <>
          {detailed ? (
            <RoleSelectorDetailedTrigger
              placeholder={placeholder}
              disabled={disabled}
            />
          ) : (
            <EntitySelectorTrigger
              placeholder={placeholder}
              size={toEntityTriggerSize(size)}
              disabled={disabled}
              chips={chips}
              fill={fill}
              icon={icon}
            />
          )}
          <EntitySelectorContent size={contentSize} fit={contentFit}>
            <EntitySelectorInput />
            <EntitySelectorPool value={pool.id} />
            <EntitySelectorEmpty>No roles found</EntitySelectorEmpty>
          </EntitySelectorContent>
        </>
      )}
    </EntitySelector>
  );
}
